import { useState, useEffect, useCallback } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Search, FileCheck, RefreshCw, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import IssueInvoiceModal from './IssueInvoiceModal';

/**
 * GenerateInvoicePicker
 * Green "Generate Invoice" entrypoint for /sales-new.
 * Lists completed sales at this branch that do NOT yet have a BIR number,
 * lets the operator pick one (their Order Slip / completed sale), then opens
 * IssueInvoiceModal to assign the Sales Invoice (SI) / Charge Invoice (CI).
 *
 * Only rendered when the branch is BIR-registered — when internal-only, the
 * parent never shows the button so this stays out of the flow entirely.
 */
export default function GenerateInvoicePicker({ open, onOpenChange, branchId, birSettings, businessInfo, onIssued }) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);

  const fetchSales = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const res = await api.get('/invoices', { params: { branch_id: branchId, limit: 100 } });
      const list = (res.data.invoices || []).filter(s =>
        !s.bir_invoice_number &&
        s.status !== 'voided' &&
        s.status !== 'for_preparation'
      );
      setSales(list);
    } catch {
      toast.error('Failed to load sales');
    }
    setLoading(false);
  }, [branchId]);

  useEffect(() => {
    if (open) { setQuery(''); setSelected(null); fetchSales(); }
  }, [open, fetchSales]);

  const filtered = sales.filter(s => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (s.invoice_number || '').toLowerCase().includes(q) ||
           (s.customer_name || '').toLowerCase().includes(q);
  });

  const pick = (s) => {
    setSelected(s);
    setIssueOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="generate-invoice-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>
              <FileCheck size={18} /> Generate Sales / Charge Invoice
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Pick an Order Slip / completed sale that has no BIR invoice yet. A Sales Invoice (paid) or Charge Invoice (credit) will be assigned.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              data-testid="generate-invoice-search"
              className="pl-8 h-9 text-sm"
              placeholder="Search by reference number or customer…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>

          <ScrollArea className="h-[320px] -mx-1 px-1">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
                <RefreshCw size={15} className="animate-spin mr-2" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">
                No un-invoiced sales found. Complete a sale or Order Slip first.
              </div>
            ) : (
              <div className="space-y-1.5">
                {filtered.map(s => {
                  const isCredit = s.payment_type === 'credit' || s.payment_type === 'partial';
                  return (
                    <button
                      key={s.id}
                      data-testid={`gen-invoice-row-${s.id}`}
                      onClick={() => pick(s)}
                      className="w-full text-left rounded-lg border p-3 hover:border-[#1A4D2E] hover:bg-emerald-50/50 transition-colors flex items-center justify-between gap-3"
                      style={{ borderColor: 'var(--border-default)' }}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-slate-700">{s.invoice_number}</span>
                          <Badge className={`text-[10px] ${isCredit ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {isCredit ? 'Charge Invoice' : 'Sales Invoice'}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{s.customer_name || 'Walk-in'}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-semibold text-sm text-[#1A4D2E]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatPHP(s.grand_total)}
                        </span>
                        <ArrowRight size={14} className="text-slate-300" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <IssueInvoiceModal
        open={issueOpen}
        onOpenChange={(v) => {
          setIssueOpen(v);
          if (!v) { setSelected(null); fetchSales(); }
        }}
        invoice={selected}
        birSettings={birSettings}
        businessInfo={businessInfo}
        onSuccess={() => { if (onIssued) onIssued(); }}
      />
    </>
  );
}
